import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  slugFrom, slugIsValid, withMember, withoutMember, blockedBecause, createBody, rankByName,
  type Candidate,
} from '../lib/community.js';

export interface CreateCommunityProps {
  /** Who you follow, live or not — the pool before anybody searches. */
  readonly follows: readonly Candidate[];
  readonly onClose: () => void;
  /** Where to go once it exists. */
  readonly onCreated: (slug: string) => void;
}

interface Credential { readonly clientId: string; readonly token: string }

/**
 * Make a community.
 *
 * PICKING IS NOT LIMITED TO WHO IS LIVE, which is the point of the screen. A
 * community is a standing list with a name and a link; if its membership
 * could only be chosen from whoever happens to be streaming, it would have to
 * be rebuilt every evening and would never contain the person you started it
 * for. So the pool is everyone you follow, and past that, Twitch.
 *
 * The three fields are asked for in the order they are decided: what it is
 * called, where it lives, who is in it. The link is suggested from the name
 * and stays editable, because a name is what a room is CALLED and a link is
 * what it is REACHED at — welding them together breaks the first time
 * somebody renames a room that already has links out in the world.
 */
export function CreateCommunity({ follows, onClose, onCreated }: CreateCommunityProps): ReactNode {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  // Until somebody edits the link themselves, it follows the name.
  const [slugTouched, setSlugTouched] = useState(false);
  const [visibility, setVisibility] = useState<'public' | 'private'>('private');
  const [members, setMembers] = useState<readonly Candidate[]>([]);
  const [term, setTerm] = useState('');
  const [hits, setHits] = useState<readonly Candidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [credential, setCredential] = useState<Credential | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** The handle the server has already refused, so it is not offered again. */
  const [taken, setTaken] = useState<readonly string[]>([]);
  const [saving, setSaving] = useState(false);

  const effectiveSlug = slugTouched ? slug : slugFrom(name);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  /*
   * A Helix token from the same cookie session the console already has.
   * Searching Twitch needs one; the follow list does not, so a failure here
   * costs the search box and nothing else.
   */
  useEffect(() => {
    let cancelled = false;
    void fetch('/v1/auth/twitch/chat-credential', { method: 'POST', credentials: 'same-origin' })
      .then(async (r) => (r.ok ? ((await r.json()) as Record<string, string>) : null))
      .then((body) => {
        if (cancelled || !body?.access_token || !body.client_id) return;
        setCredential({ clientId: body.client_id, token: body.access_token });
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  /*
   * Debounced, and the LAST request wins.
   *
   * Typing "stef" fires four searches and they do not come back in order; a
   * naive version renders whichever answered last, which is regularly the
   * answer to "ste".
   */
  const seq = useRef(0);
  useEffect(() => {
    const query = term.trim();
    if (query.length < 2 || credential === null) { setHits([]); setSearching(false); return undefined; }
    setSearching(true);
    const mine = ++seq.current;
    const timer = setTimeout(() => {
      const url = `https://api.twitch.tv/helix/search/channels?first=20&query=${encodeURIComponent(query)}`;
      void fetch(url, {
        headers: { authorization: `Bearer ${credential.token}`, 'client-id': credential.clientId },
      })
        .then(async (r) => (r.ok ? ((await r.json()) as { data?: Record<string, unknown>[] }) : null))
        .then((body) => {
          if (mine !== seq.current) return;
          setHits(rankByName((body?.data ?? []).flatMap((row) => {
            const login = row.broadcaster_login;
            if (typeof login !== 'string' || login.length === 0) return [];
            return [{
              login: login.toLowerCase(),
              displayName: typeof row.display_name === 'string' ? row.display_name : login,
              avatarUrl: typeof row.thumbnail_url === 'string' ? row.thumbnail_url : null,
              live: row.is_live === true,
            }];
          }), query));
          setSearching(false);
        })
        .catch(() => { if (mine === seq.current) setSearching(false); });
    }, 250);
    return () => { clearTimeout(timer); };
  }, [term, credential]);

  const chosen = useMemo(() => new Set(members.map((m) => m.login)), [members]);
  const pool = term.trim().length >= 2
    ? hits
    // Live first, so the people you might watch tonight are at the top of a
    // list that is otherwise alphabetical by accident of the follow API.
    : [...follows].sort((a, b) => Number(b.live) - Number(a.live));
  const blocked = taken.includes(effectiveSlug)
    ? 'That link is taken. Try another.'
    : blockedBecause({ name, slug: effectiveSlug, members });

  const submit = (): void => {
    if (blocked !== null || saving) return;
    setSaving(true);
    setNotice(null);
    void fetch('/v1/rooms', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(createBody({ name, slug: effectiveSlug, members }, visibility)),
    })
      .then((r) => {
        if (r.ok) { onCreated(effectiveSlug); return; }
        /*
         * 409 is the only failure here a person can act on, and it is the
         * only one that must not say "try again" — the same handle will be
         * taken forever. Remembering it turns the message into a rule the
         * form then enforces, so pressing Create twice cannot repeat it.
         */
        if (r.status === 409) setTaken((list) => [...list, effectiveSlug]);
        setNotice(r.status === 409
          ? 'That link is taken. Try another.'
          : 'That did not save. Try again in a moment.');
        setSaving(false);
      })
      .catch(() => { setNotice('That did not save. Try again in a moment.'); setSaving(false); });
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4"
      onMouseDown={onClose}
    >
      <div
        role="dialog"
        aria-label="Create a community"
        onMouseDown={(e) => { e.stopPropagation(); }}
        className="flex max-h-[85vh] w-full max-w-[34rem] flex-col overflow-hidden rounded-xl border border-ink-800 bg-ink-950"
      >
        <div className="flex items-center justify-between border-b border-ink-800 px-4 py-3">
          <h2 className="text-sm font-semibold">Create a community</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-ink-400 hover:text-ink-200">✕</button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="space-y-3 border-b border-ink-800 p-4">
            <label className="block">
              <span className="mb-1 block text-xs text-ink-400">Name</span>
              <input
                value={name}
                onChange={(e) => { setName(e.target.value); }}
                placeholder="567 Crew"
                maxLength={80}
                className="w-full rounded-lg border border-ink-800 bg-ink-900 px-3 py-2 text-sm outline-none focus:border-ink-700"
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-xs text-ink-400">Link</span>
              <span className="flex items-center gap-1 rounded-lg border border-ink-800 bg-ink-900 px-3 py-2 focus-within:border-ink-700">
                <span className="shrink-0 text-sm text-ink-400">tomo.channel/</span>
                <input
                  value={effectiveSlug}
                  onChange={(e) => { setSlugTouched(true); setSlug(e.target.value.toLowerCase()); }}
                  placeholder="567-crew"
                  maxLength={40}
                  className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                />
              </span>
              {effectiveSlug.length > 0 && !slugIsValid(effectiveSlug) ? (
                <span className="mt-1 block text-[11px] text-warn-400">
                  3–40 lowercase letters, digits or hyphens.
                </span>
              ) : taken.includes(effectiveSlug) ? (
                // Beside the field it is about, not only in the footer — the
                // footer is where you look after pressing, and this is the
                // thing to change before pressing again.
                <span className="mt-1 block text-[11px] text-warn-400">
                  Taken. Try another.
                </span>
              ) : null}
            </label>

            <fieldset>
              <legend className="mb-1 text-xs text-ink-400">Who can find it</legend>
              <div className="flex gap-2">
                {(['private', 'public'] as const).map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => { setVisibility(value); }}
                    aria-pressed={visibility === value}
                    className={`flex-1 rounded-lg border px-3 py-2 text-left text-sm ${
                      visibility === value
                        ? 'border-ink-50 bg-ink-50 text-ink-950'
                        : 'border-ink-800 hover:border-ink-700'
                    }`}
                  >
                    {value === 'private' ? 'Private' : 'Public'}
                    <span className={`block text-[11px] ${visibility === value ? 'text-ink-400' : 'text-ink-400'}`}>
                      {value === 'private' ? 'Anyone with the link' : 'Listed on the front page'}
                    </span>
                  </button>
                ))}
              </div>
            </fieldset>
          </div>

          <div className="p-4">
            <div className="mb-2 flex items-baseline justify-between">
              <span className="text-xs text-ink-400">
                Members{members.length > 0 ? ` · ${String(members.length)}` : ''}
              </span>
              {/* Says the thing the screen exists for, once, where it matters. */}
              <span className="text-[11px] text-ink-400">Live or not</span>
            </div>

            {members.length > 0 && (
              <ul className="mb-3 flex flex-wrap gap-1.5">
                {members.map((m) => (
                  <li key={m.login}>
                    <button
                      type="button"
                      onClick={() => { setMembers(withoutMember(members, m.login)); }}
                      aria-label={`Remove ${m.displayName}`}
                      className="flex items-center gap-1.5 rounded-full border border-ink-700 bg-ink-900 py-1 pr-2 pl-1 text-xs hover:border-ink-700"
                    >
                      {m.avatarUrl
                        ? <img src={m.avatarUrl} alt="" className="h-5 w-5 rounded-full" />
                        : <span className="grid h-5 w-5 place-items-center rounded-full bg-ink-800 text-[10px]">{m.displayName.slice(0, 1).toUpperCase()}</span>}
                      {m.displayName}
                      <span className="text-ink-400">✕</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <input
              value={term}
              onChange={(e) => { setTerm(e.target.value); }}
              placeholder={credential ? 'Search Twitch…' : 'Search needs a Twitch connection'}
              disabled={credential === null}
              className="mb-2 w-full rounded-lg border border-ink-800 bg-ink-900 px-3 py-2 text-sm outline-none focus:border-ink-700 disabled:opacity-50"
            />

            <ul className="grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-1.5">
              {pool.filter((c) => !chosen.has(c.login)).slice(0, 60).map((c) => (
                <li key={c.login}>
                  <button
                    type="button"
                    onClick={() => { setMembers(withMember(members, c)); }}
                    className="flex w-full items-center gap-2 rounded-full border border-ink-800 py-1 pr-3 pl-1 text-left hover:border-ink-700"
                  >
                    {c.avatarUrl
                      ? <img src={c.avatarUrl} alt="" className="h-7 w-7 shrink-0 rounded-full" />
                      : <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-ink-800 text-xs">{c.displayName.slice(0, 1).toUpperCase()}</span>}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm leading-4">{c.displayName}</span>
                      <span className="block text-[11px] leading-4 text-ink-400">
                        {c.live ? 'Live now' : 'Offline'}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            {pool.length === 0 && (
              <p className="text-sm text-ink-400">
                {searching ? 'Searching Twitch…'
                  : term.trim().length >= 2 ? 'Nobody by that name.'
                    : 'Search for anyone on Twitch to add them.'}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-ink-800 px-4 py-3">
          <span className="min-w-0 truncate text-[11px] text-ink-400">
            {notice ?? blocked ?? `tomo.channel/${effectiveSlug}`}
          </span>
          <button
            type="button"
            onClick={submit}
            disabled={blocked !== null || saving}
            className="shrink-0 rounded-lg bg-ink-50 px-4 py-2 text-sm font-medium text-ink-950 disabled:opacity-40"
          >
            {saving ? 'Creating…' : 'Create room'}
          </button>
        </div>
      </div>
    </div>
  );
}
